import { oc } from "@orpc/contract";
import { z } from "zod";
import { PreflightResultSchema } from "./preflight.js";
import { RunStartInputSchema } from "./runs.js";
// The RPC explorer's own storage: the signed-in account's saved and last-used inputs,
// one blob per (site, account) the server names from the session — never from the
// request. Served only while DEV_TOOLS_ENABLED is on (otherwise these routes answer
// exactly like an unknown route), and kept out of the explorer's own catalog.
export const SAVED_INPUT_LIMITS = {
    /** Saved inputs per procedure. */
    perProcedure: 10,
    /** UTF-8 bytes per input text (saved or last used). */
    bytes: 16 * 1024,
    /** Characters per saved input name. */
    nameLength: 60,
    /** UTF-8 bytes of the whole serialized document. */
    totalBytes: 128 * 1024,
};
/** Procedures whose inputs are never stored, beside every session-changing one. */
export const NEVER_STORED_INPUTS = ["identity.validateContinuation"];
export const SavedInputSchema = z.object({
    name: z.string().min(1).max(SAVED_INPUT_LIMITS.nameLength),
    text: z.string(),
});
/** The versioned document: `last` and `saved` keyed by `<area>.<name>`. */
export const SavedInputsSchema = z.object({
    v: z.literal(1),
    last: z.record(z.string(), z.string()),
    saved: z.record(z.string(), z.array(SavedInputSchema)),
});
const SavedInputsState = z.object({
    inputs: SavedInputsSchema,
    /** Opaque version for the next write; null when nothing is stored yet. */
    etag: z.string().nullable(),
});
/**
 * A run's sandbox time on the sandbox path (microVM plan row 9): how long its sandbox was
 * awake and what Azure would bill for that at list price. An estimate for /dev, never
 * charged; `runtimeChargedCents` is what the run was charged for runtime (the façade
 * ledger's Claude-style session-hour price over active turns), shown beside it.
 */
export const RunSandboxTimeSchema = z.object({
    runId: z.string(),
    sessionId: z.string(),
    tier: z.string(),
    /** Awake windows: create or resume to a stop or the release. */
    windows: z.number().int().nonnegative(),
    /** Windows whose end is last activity plus the 300 s auto-suspend, not an observed moment. */
    estimatedWindows: z.number().int().nonnegative(),
    /** A window is still open; it counts up to now or the auto-suspend bound. */
    open: z.boolean(),
    awakeSeconds: z.number().nonnegative(),
    /** US dollars at the ACA list rates; null for a tier the server does not size. */
    estimatedUsd: z.number().nonnegative().nullable(),
    runtimeChargedCents: z.number().nonnegative(),
});
export const SandboxTimeTotalSchema = z.object({
    sessions: z.number().int().nonnegative(),
    windows: z.number().int().nonnegative(),
    awakeSeconds: z.number().nonnegative(),
    estimatedUsd: z.number().nonnegative(),
});
export const SandboxTimeResultSchema = z.object({
    runs: z.array(RunSandboxTimeSchema),
    /** Every sandbox session of the account in the last 24 hours, windows clipped to the period. */
    last24h: SandboxTimeTotalSchema,
});
/** Runs a sandbox-time read takes at once (the /dev picker lists 20). */
export const SANDBOX_TIME_MAX_RUNS = 50;
export const devtools = {
    getSavedInputs: oc
        .route({
        summary: "The RPC explorer's saved inputs",
        description: "This account's saved and last-used explorer inputs on this site, with the version a write must name. Dev tools only; needs a session.",
    })
        .meta({ access: "read", keys: false })
        .output(SavedInputsState),
    putSavedInputs: oc
        .route({
        summary: "Replace the RPC explorer's saved inputs",
        description: "Writes the whole document if the stored version still equals etag (null: nothing stored yet), else 409 saved_inputs_conflict. Unknown procedures are pruned; caps and never-stored procedures are refused. Dev tools only; needs a session.",
    })
        .meta({ access: "write", keys: false })
        .input(SavedInputsState)
        .output(SavedInputsState),
    preflightProject: oc
        .route({
        summary: "Preflight a project's run",
        description: "Runs the checks runs.start makes — the revision, agent.yaml, the overlay, the engine, the roster, the mission, the cap, and the engine's own checker and compile — without starting a run: nothing is written and no provider is called. Known blockers are a normal answer (ready: false), not an error; a project you cannot see is 404. Provider availability, Claude Managed Agents' acceptance and live MCP discovery stay unknown until a run starts. Dev tools only; needs a session.",
    })
        .meta({ access: "read", keys: false })
        .input(RunStartInputSchema)
        .output(PreflightResultSchema),
    sandboxTime: oc
        .route({
        summary: "Sandbox time for runs",
        description: "Per run on the sandbox path: the sandbox's awake windows, awake seconds and an estimate of what Azure bills for them at list price, beside the runtime the run was charged; plus the account's sandbox sessions in the last 24 hours. The estimate is never charged. Runs without a sandbox are left out. Dev tools only; needs a session.",
    })
        .meta({ access: "read", keys: false })
        .input(z.object({ runIds: z.array(z.uuid()).max(SANDBOX_TIME_MAX_RUNS) }))
        .output(SandboxTimeResultSchema),
};
//# sourceMappingURL=devtools.js.map